// A client in the studio: their name, their projects, their link and
// password, and deleting them.
import * as React from "react"
import { Dots } from "../../toolkit/Dots"
import { fmtMoney, studioClient, studioDelete, studioNewPassword, studioRemove, studioSave, type FullClient } from "../api"
import { go } from "../route"
import { totals } from "../../lib/auth"
import { BackLink, ProjectRow, Secret, useEntrance } from "./Studio"
import { Confirm, DeleteBar, Field, SaveBar, SelectToggle, plural, slugify, studioPath, useLeaveGuard, useSelection, type SaveState } from "./parts"

export function ClientEdit({ slug, onChanged }: { slug: string; onChanged: () => void }) {
    const [client, setClient] = React.useState<FullClient | "gone" | null>(null)
    React.useEffect(() => {
        studioClient(slug).then((c) => setClient(c ?? "gone"))
    }, [slug])
    if (!client) return null
    if (client === "gone") return <p className="p-quiet nto-mono">There's no client at /{slug}.</p>
    return <Editor client={client} onChanged={onChanged} />
}

function Editor({ client, onChanged }: { client: FullClient; onChanged: () => void }) {
    const root = React.useRef<HTMLElement>(null)
    useEntrance(root)
    const [saved, setSaved] = React.useState({ name: client.name, logo: client.logo, order: client.projects.map((p) => p.id).join(",") })
    const [name, setName] = React.useState(client.name)
    const [logo, setLogo] = React.useState<string | null>(client.logo)
    const [state, setState] = React.useState<SaveState>("idle")
    const [password, setPassword] = React.useState("")
    const [confirmDelete, setConfirmDelete] = React.useState("")
    const [projects, setProjects] = React.useState(client.projects)
    const sel = useSelection()
    const picked = projects.filter((p) => sel.has(p.id))
    // a new name brings a new address (the old link keeps forwarding)
    const address = slugify(name)
    // (or, for a name saved earlier, on request: "Change link to /…")
    const [follow, setFollow] = React.useState(false)
    const moving = (follow || name.trim() !== saved.name) && !!address && address !== client.slug
    const reordered = projects.map((p) => p.id).join(",") !== saved.order
    const dirty = name.trim() !== saved.name || logo !== saved.logo || moving || reordered
    const shift = (i: number, to: number) =>
        setProjects((ps) => {
            const n = [...ps]
            const [x] = n.splice(i, 1)
            n.splice(to, 0, x)
            return n
        })
    useLeaveGuard(dirty)
    const link = `${location.origin}/${client.slug}`

    const save = async () => {
        setState("saving")
        const r = await studioSave({ slug: client.slug, name, logo, projects, rename: moving ? address : undefined })
        if ("error" in r) return setState({ error: r.error })
        if (r.slug !== client.slug) {
            onChanged()
            return go(studioPath(r.slug), { replace: true, quiet: true })
        }
        setSaved({ name: name.trim(), logo, order: projects.map((p) => p.id).join(",") })
        setState("saved")
        onChanged()
    }

    return (
        <main ref={root} className="p-page">
            <section className="p-hero">
                <BackLink to={studioPath()} label="All clients" />
                <div className="p-meta nto-mono" data-r>
                    {client.no > 0 && <span title="Their number, in new project references">Client {String(client.no).padStart(2, "0")}</span>}
                    <span>
                        /{client.slug}
                        {moving && <span className="e-moving"> → /{address}</span>}
                    </span>
                    {!moving && address && address !== client.slug && (
                        <button type="button" className="e-link e-follow nto-mono" onClick={() => (setFollow(true), setState("idle"))}>
                            Change link to /{address}
                        </button>
                    )}
                    <a className="e-link" href={"/" + client.slug} target="_blank" rel="noopener">
                        View as the client sees it ↗
                    </a>
                </div>
                <div className="o-name p-title e-name" data-r>
                    {logo && <img className="o-logo" src={logo} alt="" />}
                    <input className="p-title e-title-in" value={name} onChange={(e) => (setName(e.target.value), setState("idle"))} aria-label="Client name" />
                </div>
            </section>

            <section className="p-section" data-r>
                <h2 className="p-section-label nto-label">Logo</h2>
                <div className="e-body e-logo-row">
                    <span className={"e-logo-box" + (logo ? "" : " e-logo-none")}>{logo ? <img src={logo} alt="" /> : <span className="nto-mono">No logo</span>}</span>
                    <div className="e-actions">
                        <label className="e-btn e-file">
                            {logo ? "Replace" : "Upload logo"}
                            <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                onChange={async (e) => {
                                    const f = e.target.files?.[0]
                                    e.target.value = ""
                                    if (!f) return
                                    const url = await squareLogo(f).catch(() => null)
                                    if (url) (setLogo(url), setState("idle"))
                                    else setState({ error: "Couldn't read that image" })
                                }}
                            />
                        </label>
                        {logo && (
                            <button type="button" className="e-btn e-plain" onClick={() => (setLogo(null), setState("idle"))}>
                                No logo
                            </button>
                        )}
                    </div>
                    <p className="e-hint nto-mono">Shown in a rounded square beside their name. Square images work best; others are cropped to the middle.</p>
                </div>
            </section>

            <section className="p-section" data-r>
                <h2 className="p-section-label nto-label">Projects</h2>
                <div className="e-body">
                    <ul className="e-projects e-projects-flat">
                        {projects.map((p, i) => {
                            const a = client.approvals[p.id]
                            return (
                                <li key={p.id} className="e-project-li">
                                    <ProjectRow
                                        href={studioPath(client.slug, p.id)}
                                        selecting={sel.on}
                                        on={sel.has(p.id)}
                                        onPick={() => sel.toggle(p.id)}
                                        title={p.title}
                                        reference={p.reference}
                                        status={a ? { text: "Approved", tone: "now" } : { text: "Estimate to approve", tone: "act" }}
                                        money={fmtMoney(totals(p.estimate).total, p.estimate.currency, false)}
                                    />
                                    {!sel.on && projects.length > 1 && (
                                        <span className="e-order" title="The order their projects are shown in">
                                            <button type="button" disabled={i === 0} onClick={() => (shift(i, i - 1), setState("idle"))} aria-label="Move up">
                                                ↑
                                            </button>
                                            <button type="button" disabled={i === projects.length - 1} onClick={() => (shift(i, i + 1), setState("idle"))} aria-label="Move down">
                                                ↓
                                            </button>
                                        </span>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                    <div className="e-list-actions">
                        {!sel.on && (
                            <button type="button" className="e-add nto-mono" onClick={() => go(studioPath(client.slug, "new"))}>
                                + New project
                            </button>
                        )}
                        {projects.length > 0 && <SelectToggle on={sel.on} onStart={sel.start} onStop={sel.stop} />}
                    </div>
                </div>
            </section>

            <section className="p-section" data-r>
                <h2 className="p-section-label nto-label">Access</h2>
                <div className="e-body">
                    {password ? (
                        <Secret link={link} password={password} />
                    ) : (
                        <div className="e-grid">
                            <Field label="Their link" wide>
                                <span className="e-static nto-mono">{link}</span>
                            </Field>
                            <div className="e-actions">
                                <Confirm
                                    label="New password"
                                    sure="Their old password stops working. Sure?"
                                    onYes={async () => {
                                        const p = await studioNewPassword(client.slug)
                                        if (p) setPassword(p)
                                        else setState({ error: "Couldn't make a new password, try again?" })
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="p-section e-quiet-section" data-r>
                <h2 className="p-section-label nto-label">Delete</h2>
                <div className="e-body e-grid">
                    <Field label={`Type ${client.slug} to delete this client and all their projects`} wide>
                        <input className="e-in nto-mono" value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} placeholder={client.slug} />
                    </Field>
                    <div className="e-actions">
                        <button
                            type="button"
                            className="e-btn e-danger"
                            disabled={confirmDelete !== client.slug}
                            onClick={async () => {
                                if (await studioDelete(client.slug)) {
                                    onChanged()
                                    go(studioPath())
                                } else setState({ error: "Couldn't delete, try again?" })
                            }}
                        >
                            Delete client
                        </button>
                    </div>
                </div>
            </section>

            {sel.on ? (
                <DeleteBar
                    count={picked.length}
                    summary={`${plural(picked.length, "project")} selected`}
                    question={`Delete ${picked.length === 1 ? `"${picked[0].title}"` : plural(picked.length, "project")}?`}
                    onCancel={sel.stop}
                    onDelete={async () => {
                        const ids = picked.map((p) => p.id)
                        const ok = await studioRemove([{ slug: client.slug, projects: ids }])
                        if (ok) {
                            setProjects((ps) => ps.filter((p) => !ids.includes(p.id)))
                            setSaved((v) => ({ ...v, order: v.order.split(",").filter((id) => !ids.includes(id)).join(",") }))
                            sel.stop()
                            onChanged()
                        }
                        return ok
                    }}
                />
            ) : (
            <SaveBar dirty={dirty} state={state} note={moving ? `Their link becomes /${address}; the old one forwards` : undefined} onSave={save} onDiscard={() => (setName(saved.name), setLogo(saved.logo), setFollow(false), setProjects(saved.order.split(",").map((id) => client.projects.find((p) => p.id === id)!).filter(Boolean)), setState("idle"))} />
            )}
        </main>
    )
}

/** an uploaded image as a 256px square (cropped to the middle), small enough to store */
async function squareLogo(file: File): Promise<string> {
    const url = URL.createObjectURL(file)
    try {
        const img = new Image()
        img.src = url
        await img.decode()
        const size = 256
        const c = document.createElement("canvas")
        c.width = c.height = size
        const g = c.getContext("2d")!
        const w = img.naturalWidth || size,
            h = img.naturalHeight || size
        const s = Math.max(size / w, size / h)
        g.imageSmoothingQuality = "high"
        g.drawImage(img, (size - w * s) / 2, (size - h * s) / 2, w * s, h * s)
        const webp = c.toDataURL("image/webp", 0.9)
        return webp.startsWith("data:image/webp") ? webp : c.toDataURL("image/png")
    } finally {
        URL.revokeObjectURL(url)
    }
}
